import * as React from 'react';
import { useState, useEffect } from 'react';
import { cleanupPwa } from './pwa';

import carePlusLogoImg from './static/images/care+-logo.svg';
import checkmarkImg from './static/images/checkmark.svg';
import exclaimMarkImg from './static/images/exclaimmark.svg';
import hchbLogoImg from './static/images/hchb-logo.svg';
import pwaInstallImg from './static/images/pwa-install.jpg';
import xMarkImg from './static/images/xmark.svg';

interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const isMobileDevice = (): boolean => {
    const userAgent = navigator.userAgent.toLowerCase();
    return /android|iphone|ipad|ipod/.test(userAgent);
};

export const PWAInstaller: React.FC = () => {
    const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
    const [isInstallable, setIsInstallable] = useState(false);
    const [isInstalled, setIsInstalled] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [showFailureMessage, setShowFailureMessage] = useState(false);

    useEffect(() => {
        const handler = (e: Event) => {
            e.preventDefault();
            setInstallPrompt(e as BeforeInstallPromptEvent);
            setIsInstallable(true);
        };

        window.addEventListener('beforeinstallprompt', handler);

        // Check if app is already installed
        if (window.matchMedia('(display-mode: standalone)').matches) {
            setIsInstalled(true);
        }

        return () => {
            window.removeEventListener('beforeinstallprompt', handler);
        };
    }, []);

    const handleInstallClick = async () => {
        if (!installPrompt) return;

        try {

            // clear out any previous cached data, even if they cancelled the install
            cleanupPwa();

            await installPrompt.prompt();
            const choiceResult = await installPrompt.userChoice;

            if (choiceResult.outcome === 'accepted') {
                setIsInstalled(true);
                setIsInstallable(false);

                setShowSuccessMessage(true);
            }
            setInstallPrompt(null);
        } catch (error) {
            console.error('Error installing PWA:', error);
            setShowFailureMessage(true);
        }
    };

    if (window.matchMedia('(orientation: portrait)').matches) {
        return (
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: '#FFFFFE',
                    display: 'flex',
                    flexDirection: 'column',
                    fontFamily: '"Noto Sans", sans-serif'
                }}
            >
                <div
                    style={{
                        flex: '1 1 auto',
                        minHeight: '0px',
                        overflow: 'hidden'
                    }}
                >
                    <img
                        src={pwaInstallImg}
                        style={{
                            height: '100%',
                            width: '100%',
                            objectFit: 'cover',
                            objectPosition: 'center'
                        }}
                    />
                </div>
                <div
                    style={{
                        flex: '0',
                        marginTop: '30px',
                        marginLeft: '108px',
                        display: 'grid',
                        placeItems: 'left'
                    }}
                >
                    <div style={{ flex: '0', alignItems: 'left', marginBottom: '20px' }}>
                        <img
                            src={carePlusLogoImg}
                            style={{ height: '84px', width: 'auto' }}
                        />
                    </div>
                    <div
                        style={{
                            fontSize: '18px',
                            fontWeight: '300',
                            lineHeight: '26px',
                            letterSpacing: '0%',
                            textAlign: 'left',
                            color: '#374151',
                            width: '264px',
                            height: '75px',
                            marginBottom: '20px'
                        }}
                    >
                        Scheduling and documenting care has never been easier or more enjoyable.
                    </div>
                    <div
                        style={{
                            fontSize: '16px',
                            fontWeight: '400',
                            lineHeight: '22px',
                            letterSpacing: '0%',
                            textAlign: 'left',
                            color: '#6B7280',
                            width: '252px',
                            height: '109px',
                            marginBottom: '38px'
                        }}
                    >
                        HCHB's Care+ delights and simplifies your daily routine– from scheduling to care documentation, and everything in between.
                    </div>
                    <button
                        onClick={handleInstallClick}
                        style={{
                            backgroundColor: '#0D9488',
                            color: 'white',
                            border: 'none',
                            padding: '14px 12px 14px 12px',
                            borderRadius: '8px',
                            gap: '8px',
                            cursor: 'pointer',
                            width: '113px',
                            height: '48px'
                        }}
                    >
                        Install Care+
                    </button>
                    <img
                        src={hchbLogoImg}
                        style={{
                            height: 'auto',
                            width: '205px',
                            marginTop: '38px',
                            marginBottom: '25px'
                        }}
                    />
                </div>
                {showSuccessMessage ? getSuccessToast(setShowSuccessMessage) : null}
                {showFailureMessage ? getFailureToast(setShowFailureMessage, handleInstallClick) : null}
            </div>
        );
    }

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: '#FFFDFE',
                display: 'flex',
                fontFamily: '"Noto Sans", sans-serif'
            }}
        >
            <div
                style={{
                    flex: '0 0 289px',
                    margin: '0px 70px 100px 130px',
                    display: 'grid',
                    placeItems: 'left'
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'left',
                        position: 'absolute',
                        bottom: '86px'
                    }}
                >
                    <div style={{ flex: '1 1 auto' }}></div>
                    <div style={{ flex: '0', alignItems: 'left', marginBottom: '22px' }}>
                        <img
                            src={carePlusLogoImg}
                            style={{ height: '84px', width: 'auto' }}
                        />
                    </div>
                    <div
                        style={{
                            fontSize: '18px',
                            fontWeight: '300',
                            lineHeight: '26px',
                            letterSpacing: '0%',
                            textAlign: 'left',
                            color: '#374151',
                            width: '264px',
                            height: '75px',
                            marginBottom: '28px'
                        }}
                    >
                        Scheduling and documenting care has never been easier or more enjoyable.
                    </div>
                    <div
                        style={{
                            fontSize: '16px',
                            fontWeight: '400',
                            lineHeight: '22px',
                            letterSpacing: '0%',
                            textAlign: 'left',
                            color: '#6B7280',
                            width: '252px',
                            height: '109px',
                            marginBottom: '38px'
                        }}
                    >
                        HCHB's Care+ delights and simplifies your daily routine– from scheduling to care documentation, and everything in between.
                    </div>
                    <button
                        onClick={handleInstallClick}
                        style={{
                            backgroundColor: '#0D9488',
                            color: 'white',
                            border: 'none',
                            padding: '14px 12px 14px 12px',
                            borderRadius: '8px',
                            gap: '8px',
                            cursor: 'pointer',
                            width: '113px',
                            height: '48px',
                            marginBottom: '38px'
                        }}
                    >
                        Install Care+
                    </button>
                    <img
                        src={hchbLogoImg}
                        style={{
                            height: 'auto',
                            width: '205px',
                        }}
                    />
                </div>
            </div>
            <div
                style={{
                    flex: '1 1 auto'
                }}
            >
                <img
                    src={pwaInstallImg}
                    style={{
                        height: '100%',
                        width: '100%',
                        objectFit: 'cover',
                        objectPosition: 'center'
                    }}
                />
            </div>
            {showSuccessMessage ? getSuccessToast(setShowSuccessMessage) : null}
            {showFailureMessage ? getFailureToast(setShowFailureMessage, handleInstallClick) : null}
        </div>
    );
};

export default PWAInstaller;

function getSuccessToast(setShowSuccessMessage: React.Dispatch<React.SetStateAction<boolean>>): React.ReactElement<any, any> {
    return <div
        style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'linear-gradient( #064E3B, #047857)',
            fontFamily: '"Noto Sans", sans-serif',
            color: 'white',
            padding: '12px',
            borderRadius: '12px',
            gap: '20px',
            filter: 'drop-shadow(0 4px 4px #00000040)',
            textAlign: 'left',
            zIndex: 1000,
            maxWidth: '90%',
            width: '564px',
            animation: 'slideUp 0.3s ease-out'
        }}
    >
        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '4px' }}>
            <div
                style={{
                    width: '24px',
                    height: '24px',
                    flex: '0',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginRight: '8px'
                }}
            >
                <img
                    src={checkmarkImg}
                    style={{ width: '20px', height: '20px' }} />
            </div>
            <div style={{ flex: '1 1 auto' }}>
                <div style={{ fontSize: '16px', lineHeight: '22px', fontWeight: '600', verticalAlign: 'middle' }}>
                    Installation successful
                </div>
                <div style={{ fontSize: '14px', lineHeight: '20px', fontWeight: '400', verticalAlign: 'middle', marginBottom: '4px' }}>
                    Please close this window and launch the application from your device.
                </div>
                <div
                    style={{
                        fontSize: '14px',
                        lineHeight: '20px',
                        fontWeight: '400',
                        verticalAlign: 'middle',
                        marginBottom: '4px',
                        cursor: 'pointer'
                    }}
                    onClick={() => window.close()}
                >
                    Close window
                </div>
            </div>
            <div
                style={{
                    width: '24px',
                    height: '24px',
                    flex: '0',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginLeft: '20px',
                    cursor: 'pointer',
                }}
                onClick={() => setShowSuccessMessage(false)}
            >
                <img
                    src={xMarkImg}
                    style={{ width: '12.5px', height: '12.5px' }} />
            </div>
        </div>
        <style>
            {`
                @keyframes slideUp {
                    from {
                        transform: translate(-50%, 100%);
                        opacity: 0;
                    }
                    to {
                        transform: translate(-50%, 0);
                        opacity: 1;
                    }
                }
            `}
        </style>
    </div>;
}

function getFailureToast(setShowFailureMessage: React.Dispatch<React.SetStateAction<boolean>>, handleInstallClick: () => Promise<void>): React.ReactElement<any, any> {
    return <div
        style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'linear-gradient( #881337, #BE123C)',
            fontFamily: '"Noto Sans", sans-serif',
            color: 'white',
            padding: '12px',
            borderRadius: '12px',
            gap: '20px',
            filter: 'drop-shadow(0 4px 4px #00000040)',
            textAlign: 'left',
            zIndex: 1000,
            maxWidth: '90%',
            width: '564px',
            animation: 'slideUp 0.3s ease-out'
        }}
    >
        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '4px' }}>
            <div
                style={{
                    width: '24px',
                    height: '24px',
                    flex: '0',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginRight: '8px'
                }}
            >
                <img
                    src={exclaimMarkImg}
                    style={{ width: '20px', height: '20px' }} />
            </div>
            <div style={{ flex: '1 1 auto' }}>
                <div style={{ fontSize: '16px', lineHeight: '22px', fontWeight: '600', verticalAlign: 'middle' }}>
                    Installation failed
                </div>
                <div style={{ fontSize: '14px', lineHeight: '20px', fontWeight: '400', verticalAlign: 'middle', marginBottom: '4px' }}>
                    Please try again to install Care+
                </div>
                <div
                    style={{
                        fontSize: '14px',
                        lineHeight: '20px',
                        fontWeight: '400',
                        verticalAlign: 'middle',
                        marginBottom: '4px',
                        cursor: 'pointer'
                    }}
                    onClick={handleInstallClick}
                >
                    Re-install
                </div>
            </div>
            <div
                style={{
                    width: '24px',
                    height: '24px',
                    flex: '0',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginLeft: '20px',
                    cursor: 'pointer',
                }}
                onClick={() => setShowFailureMessage(false)}
            >
                <img
                    src={xMarkImg}
                    style={{ width: '12.5px', height: '12.5px' }} />
            </div>
        </div>
        <style>
            {`
                @keyframes slideUp {
                    from {
                        transform: translate(-50%, 100%);
                        opacity: 0;
                    }
                    to {
                        transform: translate(-50%, 0);
                        opacity: 1;
                    }
                }
            `}
        </style>
    </div>;
}